/**
 * Bridge between the in-session step checkpoints and the client-only scene cache
 * (`shared/lib/bkSceneCache`). Handles two directions:
 *
 *   persist  — after a step is captured in-session, mirror its mesh + tool
 *              session snapshot into IndexedDB so it survives a reload.
 *   resume   — on a fresh page load, rehydrate the mesh pool + tool stores for
 *              the step the URL landed on, then install the mesh into the scene.
 *
 * Keyed by `${sales_order}:${customer}` so caches never cross orders.
 */
import useEditorStore from '../../shared/stores/useEditorStore';
import useWorkflowStore from '../../shared/stores/useWorkflowStore';
import { loadBkDesignOrderContext } from '../../shared/lib/bkDesignOrderContext';
import {
  getStepScene,
  putStepScene,
  hasCachedStep,
  deleteStepScenes,
  updateResumeMeta,
} from '../../shared/lib/bkSceneCache';
import {
  serializeStepMesh,
  hydrateStepMesh,
  hasMeshForStep,
  restoreMeshForStep,
  MESH_CACHE_SCHEMA_VERSION,
} from './meshSnapshot';
import { captureSessionState, restoreSessionState } from './sessionSnapshot';
import { serializeUndoSnapshots, hydrateUndoSnapshots } from './undoSnapshot';
import useStepCheckpointStore from './stepCheckpointStore';

const LOG_PREFIX = '[SceneCacheBridge]';
const SCENE_OBJECT_WAIT_MS = 15000;
const SCENE_OBJECT_POLL_MS = 32;

function currentOrderKey() {
  const ctx = loadBkDesignOrderContext();
  return ctx ? `${ctx.sales_order}:${ctx.customer}` : null;
}

/**
 * Mirror a just-captured step into the persistent cache. Fire-and-forget:
 * resolves false (never throws) so it can't break the capture path.
 * @param {string} stepId
 */
export function persistStepSceneToCache(stepId) {
  const orderKey = currentOrderKey();
  if (!orderKey) return Promise.resolve(false);

  const mesh = serializeStepMesh(stepId);
  if (!mesh) return Promise.resolve(false);

  const record = useStepCheckpointStore.getState().getCheckpoint(stepId);
  const session = record?.session ?? captureSessionState();
  const wf = useWorkflowStore.getState();

  return putStepScene(
    orderKey,
    stepId,
    {
      schemaVersion: MESH_CACHE_SCHEMA_VERSION,
      mesh,
      session,
      // Pre-apply socket for this step's tools — without this, Undo is dead after
      // a reload. Scoped to the step so the geometry isn't duplicated per record.
      undo: serializeUndoSnapshots(stepId),
    },
    {
      stepCompletion: wf.stepCompletion,
      intent: wf.bkMainProcessIntent,
      currentStep: wf.currentStep,
    },
  );
}

/**
 * Rebuild the in-memory pool entry for `stepId` from the persistent cache,
 * without touching the scene.
 *
 * The cache is the *durable* copy of "the socket as this step left it", but until
 * now only `resumeStepFromCache` could reach it, and that runs for exactly one
 * step: the one the URL landed on. Every other step's pools are empty after a
 * reload — so "discard and go back" had nothing to revert to and quietly did
 * nothing, even though the mesh it needed was sitting in IndexedDB.
 *
 * Registers the result as an in-session checkpoint so the guards, the replay
 * anchor search and `restoreStepSession` all see it exactly as a live capture.
 *
 * @param {string} stepId
 * @returns {Promise<boolean>} whether the step now has a mesh in the pool
 */
export async function hydrateStepMeshFromCache(stepId) {
  const orderKey = currentOrderKey();
  if (!orderKey || !stepId) return false;
  if (hasMeshForStep(stepId)) return true;
  if (!hasCachedStep(orderKey, stepId)) return false;

  const record = await getStepScene(orderKey, stepId);
  if (!record?.mesh || !hydrateStepMesh(stepId, record.mesh)) return false;

  // The pre-apply snapshots for this step's own tools, so its cards keep working
  // Undo buttons after the revert lands.
  hydrateUndoSnapshots(record.undo);

  useStepCheckpointStore.getState().setCheckpoint(stepId, {
    hasMesh: true,
    session: record.session ?? null,
  });

  console.log(`${LOG_PREFIX} hydrated ${stepId} from cache`);
  return true;
}

/**
 * Drop the persisted scenes for steps whose work has just been discarded, and
 * re-publish the resume metadata so the stepper does not come back thinking they
 * are still complete.
 *
 * Without this the discard only holds until the next time one of those steps is
 * entered: the cached record still carries the post-apply mesh, the tool session
 * that says "applied", and the pre-apply undo snapshots, and whatever reads it
 * next puts all three back.
 *
 * Fire-and-forget: resolves false (never throws) so it cannot fail a revert.
 *
 * @param {string[]} stepIds
 * @returns {Promise<boolean>}
 */
export function discardStepScenesFromCache(stepIds) {
  const orderKey = currentOrderKey();
  if (!orderKey) return Promise.resolve(false);

  const wf = useWorkflowStore.getState();
  updateResumeMeta(orderKey, {
    stepCompletion: wf.stepCompletion,
    currentStep: wf.currentStep,
  });

  return deleteStepScenes(orderKey, stepIds);
}

/**
 * Load the cached pre-apply snapshots for `stepId` into the undo pool.
 *
 * Needed on *backward* navigation after a reload: `resumeStepFromCache` only runs
 * for the step the URL landed on, so stepping back to an earlier step would leave
 * that step's tools with an applied flag and nothing for Undo to restore.
 * In-memory snapshots win, so this never clobbers live session state.
 *
 * @param {string} stepId
 * @returns {Promise<number>} how many snapshots were hydrated
 */
export function hydrateUndoSnapshotsForStep(stepId) {
  const orderKey = currentOrderKey();
  if (!orderKey || !stepId) return Promise.resolve(0);
  return getStepScene(orderKey, stepId)
    .then((record) => hydrateUndoSnapshots(record?.undo))
    .catch((e) => {
      console.warn(`${LOG_PREFIX} undo snapshot hydrate failed for ${stepId}`, e);
      return 0;
    });
}

/** Resolve once ThreeScene has published a scene object, or on timeout. */
function waitForSceneObject(signal) {
  return new Promise((resolve) => {
    const started = performance.now();
    const tick = () => {
      if (signal?.aborted) return resolve(false);
      if (useEditorStore.getState().sceneRefs?.scene) return resolve(true);
      if (performance.now() - started > SCENE_OBJECT_WAIT_MS) return resolve(false);
      setTimeout(tick, SCENE_OBJECT_POLL_MS);
    };
    tick();
  });
}

/**
 * Restore the cached mesh + tool session for `stepId` after a reload.
 * Unlike the in-session restore, this waits only for the scene *object* (not a
 * loaded mesh), since on a fresh load the cached mesh is the first one in.
 *
 * @param {string} orderKey
 * @param {string} stepId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<boolean>}
 */
export async function resumeStepFromCache(orderKey, stepId, { signal } = {}) {
  const record = await getStepScene(orderKey, stepId);
  if (signal?.aborted) return false;
  if (!record?.mesh) return false;

  if (!hydrateStepMesh(stepId, record.mesh)) return false;

  // Before restoreSessionState nulls the live commands: give every tool that was
  // applied in this step a pre-apply socket to fall back on, so its Undo button
  // works on the first click after the reload.
  hydrateUndoSnapshots(record.undo);

  const store = useStepCheckpointStore.getState();
  store.setIsRestoring(true);
  try {
    const ready = await waitForSceneObject(signal);
    if (!ready || signal?.aborted) {
      console.warn(`${LOG_PREFIX} scene object not ready — resume aborted for ${stepId}`);
      return false;
    }

    if (!restoreMeshForStep(stepId)) {
      console.warn(`${LOG_PREFIX} mesh install failed for ${stepId}`);
      return false;
    }

    if (record.session) restoreSessionState(record.session);

    const editor = useEditorStore.getState();
    editor.applyAlignmentSocketPose?.();
    editor.bumpSocketTransformRevision?.();

    // Register in-session so subsequent navigation reuses the in-memory path.
    store.setCheckpoint(stepId, { hasMesh: true, session: record.session ?? null });
    store.setActiveStepId(stepId);

    console.log(`${LOG_PREFIX} resumed ${stepId} from cache`);
    return true;
  } finally {
    useStepCheckpointStore.getState().setIsRestoring(false);
  }
}

export { hasCachedStep };
